/**
 * What one experiment sends beyond name/dataset/evaluators: run settings and
 * credential references. Pure functions over
 * the form's overrides and the server's spec, so the panel only renders.
 *
 * The server re-validates all of it on start; `startBlocker` exists only so the
 * button can say *why* before the user clicks, not to decide anything new.
 */

import type { AgentSpec, RunSetting } from "@/features/agents/types";

export const isSecret = (s: RunSetting) => s.kind === "secret";

/** The value a setting shows: what the user typed, else the draft's, else the spec default. */
export function settingValue(s: RunSetting, values: Record<string, string>): string {
  return values[s.key] ?? s.default ?? "";
}

/** Non-secret settings with a value. Without a spec (it failed to load) the
 *  draft's values go back unchanged rather than being silently dropped. */
export function runSettingsPayload(
  spec: AgentSpec | undefined,
  values: Record<string, string>,
): Record<string, string> {
  if (!spec) return { ...values };
  const out: Record<string, string> = {};
  for (const s of spec.run_settings.filter((s) => !isSecret(s))) {
    const v = settingValue(s, values).trim();
    if (v) out[s.key] = v;
  }
  return out;
}

/** Keys with a picked credential — a secret setting, or any input set to read
 *  one — restricted to what the spec has. */
export function credentialRefsPayload(
  spec: AgentSpec | undefined,
  refs: Record<string, string>,
): Record<string, string> {
  const keys = spec
    ? [...spec.run_settings.filter(isSecret).map((s) => s.key), ...spec.inputs.map((i) => i.key)]
    : Object.keys(refs);
  return Object.fromEntries(keys.filter((k) => refs[k]).map((k) => [k, refs[k]!]));
}

/** Why "Run experiment" is off even though a draft could be saved, or null. */
export function startBlocker(
  spec: AgentSpec | undefined,
  values: Record<string, string>,
  refs: Record<string, string>,
): string | null {
  for (const s of spec?.run_settings ?? []) {
    if (!s.required) continue;
    if (isSecret(s) ? !refs[s.key] : !settingValue(s, values).trim()) return `Set "${s.label}" first.`;
  }
  return null;
}
